import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import AdminProfilePanel from '../../components/admin/AdminProfilePanel';
import ApprenantDashboardPanel from '../../components/apprenant/ApprenantDashboardPanel';
import ApprenantDocumentsPanel from '../../components/apprenant/ApprenantDocumentsPanel';
import ApprenantFormationsPanel from '../../components/apprenant/ApprenantFormationsPanel';
import ApprenantSubscribeFilierePanel from '../../components/apprenant/ApprenantSubscribeFilierePanel';
import ApprenantInscriptionDetailPanel from '../../components/apprenant/ApprenantInscriptionDetailPanel';
import ApprenantPaiementPanel from '../../components/apprenant/ApprenantPaiementPanel';
import ApprenantPaymentResultPanel from '../../components/apprenant/ApprenantPaymentResultPanel';
import ApprenantMesCoursPanel from '../../components/apprenant/ApprenantMesCoursPanel';
import ApprenantElearningPanel from '../../components/apprenant/ApprenantElearningPanel';
import ApprenantParrainagePanel from '../../components/apprenant/ApprenantParrainagePanel';
import ApprenantWalletPanel from '../../components/apprenant/ApprenantWalletPanel';
import ApprenantExcellencePanel from '../../components/apprenant/ApprenantExcellencePanel';
import ApprenantNotificationsPanel from '../../components/apprenant/ApprenantNotificationsPanel';
import PortalHelpPanel from '../../components/shared/PortalHelpPanel';
import { PORTAL_HELP_PATHS } from '../../config/portalHelpContent';
import type { AdminBreadcrumbSegment } from '../../components/admin/AdminBreadcrumb';
import type { AdminNavItem } from '../../types/admin';

interface ApprenantAcademiaPageProps {
  user: {
    id?: string;
    role?: string;
    telephone?: string;
    email?: string;
    profil?: {
      prenom?: string;
      nom?: string;
      photoUrl?: string | null;
    };
    profilApprenant?: Record<string, unknown>;
  };
  onLogout: () => void;
  onUserUpdated?: (user: ApprenantAcademiaPageProps['user']) => void;
}

const NAVIGATION: AdminNavItem[] = [
  { id: 'dashboard', label: 'Accueil', icon: 'dashboard', path: '/app' },
  { id: 'mes-cours', label: 'Mes cours', icon: 'list_alt', path: '/app/mes-cours' },
  { id: 'elearning', label: 'E-learning', icon: 'play_circle', path: '/app/elearning' },
  { id: 'formations', label: 'Formations', icon: 'school', path: '/app/formations' },
  { id: 'documents', label: 'Mon dossier', icon: 'folder', path: '/app/documents' },
  { id: 'notifications', label: 'Notifications', icon: 'notifications', path: '/app/notifications' },
  { id: 'parrainage', label: 'Parrainage', icon: 'groups', path: '/app/parrainage' },
  { id: 'wallet', label: 'Portefeuille', icon: 'account_balance_wallet', path: '/app/wallet' },
  { id: 'excellence', label: 'Excellence', icon: 'emoji_events', path: '/app/excellence' },
  { id: 'profile', label: 'Mon profil', icon: 'person', path: '/app/profil' },
];

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Accueil',
  mesCours: 'Mes cours',
  elearning: 'E-learning',
  formations: 'Formations',
  documents: 'Mon dossier',
  notifications: 'Notifications',
  parrainage: 'Parrainage',
  wallet: 'Mon portefeuille',
  excellence: 'Excellence & employabilité',
  aide: 'Centre d’aide',
  profile: 'Mon profil',
  inscription: 'Inscription',
  paiement: 'Paiement',
  resultat: 'Résultat paiement',
};

export default function ApprenantAcademiaPage({ user, onLogout, onUserUpdated }: ApprenantAcademiaPageProps) {
  const location = useLocation();

  const fullName = [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ');

  const viewKey = useMemo(() => {
    if (location.pathname.startsWith('/app/parrainage')) return 'parrainage';
    if (location.pathname.startsWith('/app/profil')) return 'profile';
    if (location.pathname.includes('/paiement')) return 'paiement';
    if (location.pathname.startsWith('/app/paiement/resultat')) return 'resultat';
    if (location.pathname.startsWith('/app/inscription')) return 'inscription';
    if (location.pathname.startsWith('/app/formations')) return 'formations';
    if (location.pathname.startsWith('/app/documents')) return 'documents';
    if (location.pathname.startsWith('/app/notifications')) return 'notifications';
    if (location.pathname.startsWith('/app/mes-cours')) return 'mesCours';
    if (location.pathname.startsWith('/app/elearning')) return 'elearning';
    if (location.pathname.startsWith('/app/wallet')) return 'wallet';
    if (location.pathname.startsWith('/app/excellence')) return 'excellence';
    if (location.pathname.startsWith('/app/aide')) return 'aide';
    return 'dashboard';
  }, [location.pathname]);

  const breadcrumb: AdminBreadcrumbSegment[] = [
    { label: 'Espace apprenant' },
    { label: BREADCRUMB_LABELS[viewKey] ?? 'Accueil' },
  ];

  if (location.pathname.startsWith('/app/frais-inscription')) {
    return <Navigate to="/app/formations" replace />;
  }

  if (location.pathname.startsWith('/app/finances')) {
    return <Navigate to="/app/wallet?tab=historique" replace />;
  }

  return (
    <AdminLayout
      navigation={NAVIGATION}
      user={{
        name: fullName || user.telephone || 'Apprenant',
        subtitle: 'Portail apprenant',
        photoUrl: user.profil?.photoUrl,
        prenom: user.profil?.prenom,
        nom: user.profil?.nom,
      }}
      onLogout={onLogout}
      searchQuery=""
      onSearchChange={() => {}}
      breadcrumb={breadcrumb}
      brandAlt="JCONSULT MY Academia"
      helpPath={PORTAL_HELP_PATHS.apprenant}
    >
      <Routes>
        <Route index element={<ApprenantDashboardPanel />} />
        <Route path="mes-cours" element={<ApprenantMesCoursPanel />} />
        <Route path="elearning" element={<ApprenantElearningPanel />} />
        <Route path="formations" element={<ApprenantFormationsPanel />} />
        <Route path="formations/subscribe/:filiereId" element={<ApprenantSubscribeFilierePanel />} />
        <Route path="inscription/:id" element={<ApprenantInscriptionDetailPanel />} />
        <Route path="inscription/:id/paiement" element={<ApprenantPaiementPanel />} />
        <Route path="paiement/resultat" element={<ApprenantPaymentResultPanel />} />
        <Route path="documents" element={<ApprenantDocumentsPanel />} />
        <Route path="notifications" element={<ApprenantNotificationsPanel />} />
        <Route path="parrainage" element={<ApprenantParrainagePanel />} />
        <Route path="wallet" element={<ApprenantWalletPanel />} />
        <Route path="finances" element={<Navigate to="/app/wallet?tab=historique" replace />} />
        <Route path="excellence" element={<ApprenantExcellencePanel />} />
        <Route path="aide" element={<PortalHelpPanel portal="apprenant" />} />
        <Route
          path="profil"
          element={
            <AdminProfilePanel user={user} onUserUpdated={onUserUpdated} variant="apprenant" />
          }
        />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AdminLayout>
  );
}
